import {
  Autocomplete,
  Box,
  Button,
  SxProps,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import { AssigneeAvatar } from "./AssigneeAvatar";
import { AssigneeOption } from "./types";

interface AssignedToSelectProps {
  isEditing: boolean;
  assignedTo: AssigneeOption;
  isUnassigned: boolean;
  options: AssigneeOption[];
  onOpen: (event: React.MouseEvent) => void;
  onClose: () => void;
  onChange: (event: React.SyntheticEvent, value: AssigneeOption | null) => void;
  readOnly?: boolean;
  buttonSx?: SxProps<Theme>;
  autocompleteSx?: SxProps<Theme>;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  label?: string;
}

export const AssignedToSelect = ({
  isEditing,
  assignedTo,
  isUnassigned,
  options,
  onOpen,
  onClose,
  onChange,
  readOnly = false,
  buttonSx,
  label,
  autocompleteSx,
  onMouseEnter,
  onMouseLeave,
}: AssignedToSelectProps) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {isEditing ? (
        <Autocomplete<AssigneeOption>
          open
          options={options}
          value={assignedTo}
          getOptionLabel={(o) => o.fullName}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          onChange={onChange}
          onClose={onClose}
          fullWidth
          sx={{ ...autocompleteSx }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              size="small"
              variant="outlined"
              autoFocus
              onKeyDown={(e) => e.key === "Escape" && onClose()}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "normal", lineHeight: 1.3 }}
                  >
                    {option.fullName}
                  </Typography>
                  {option.workloadCount !== undefined && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.3,
                      }}
                    >
                      {option.workloadCount} plans
                    </Typography>
                  )}
                </Box>
              </Box>
            </li>
          )}
        />
      ) : (
        <Button
          variant="text"
          size="small"
          onClick={onOpen}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          fullWidth
          disabled={readOnly}
          sx={{
            textTransform: "none",
            paddingY: 2,
            paddingRight: 2,
            paddingLeft: 2,
            marginLeft: -2,
            gap: 1,
            justifyContent: "flex-start",
            ...buttonSx,
          }}
        >
          <AssigneeAvatar
            fullName={assignedTo.fullName ?? ""}
            isUnassigned={isUnassigned}
          />
          <Typography variant="body2" noWrap>
            {assignedTo.fullName}
          </Typography>
        </Button>
      )}
    </Box>
  );
};
